import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { invoiceAPI } from '../../services/api';
import { OrderDetailsSkeleton, ErrorState } from '../../components/skeletons';

/**
 * Vendor-facing invoice detail. Renders a plain HTML document that prints
 * cleanly — a "Print / Save as PDF" button uses the browser's print dialog
 * so vendors can archive PDFs without us shipping a PDF library.
 */
const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  back: { fontSize: 13, color: 'var(--muted)', textDecoration: 'none', marginBottom: 16, display: 'inline-block' },
  sheet: { background: '#fff', borderRadius: 14, padding: 40, maxWidth: 820, margin: '0 auto', border: '1px solid var(--border)', boxShadow: '0 2px 14px rgba(0,0,0,0.04)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, paddingBottom: 20, borderBottom: '2px solid #1B5C3A' },
  brand: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 900, color: '#1B5C3A' },
  brandDot: { color: '#F5A623' },
  tag: { display: 'inline-block', background: '#FFF8E7', color: '#B88200', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, marginBottom: 6 },
  metaRight: { textAlign: 'right', fontSize: 13, color: 'var(--muted)' },
  sectionTitle: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--muted)', marginBottom: 8 },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginBottom: 28 },
  addr: { fontSize: 13, lineHeight: 1.6, color: 'var(--char)' },
  addrName: { fontWeight: 700, marginBottom: 4 },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: 20 },
  th: { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6, borderBottom: '2px solid #1B5C3A', background: '#FAF8F3' },
  td: { padding: '12px 12px', fontSize: 13, color: 'var(--char)', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  tdRight: { textAlign: 'right' },
  totalsBox: { marginLeft: 'auto', width: '50%', minWidth: 320 },
  totalRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 12px', fontSize: 13 },
  totalLabel: { color: 'var(--muted)' },
  totalVal: { color: 'var(--char)', fontWeight: 600 },
  grandRow: { borderTop: '2px solid #1B5C3A', marginTop: 8, paddingTop: 12 },
  grandLabel: { fontSize: 14, fontWeight: 700, color: 'var(--char)' },
  grandVal: { fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 900, color: '#1B5C3A' },
  footer: { marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 },
  btn: { background: '#1B5C3A', color: '#fff', border: 'none', padding: '10px 22px', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  actions: { maxWidth: 820, margin: '0 auto 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
};

const printCss = `
  @media print {
    @page {
      size: auto;
      margin: 0mm;
    }
    body {
      background: #fff !important;
      margin: 1.6cm !important;
    }
    .kj-no-print { display: none !important; }
    .kj-sheet { border: none !important; box-shadow: none !important; padding: 20px !important; }
  }
`;

const fmtAmt = (v) => `\u20B9${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    invoiceAPI.get(id)
      .then(r => setInvoice(r.data.data))
      .catch(err => setError(err.response?.data?.message || 'Failed to load invoice.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <OrderDetailsSkeleton />;
  if (error || !invoice) return (
    <div style={{ padding: 24, maxWidth: 600, margin: '40px auto' }}>
      <ErrorState
        title="Invoice Not Found"
        message={error || "The requested tax invoice could not be retrieved."}
        onRetry={() => navigate('/vendor/invoices')}
      />
    </div>
  );

  const isIGST = invoice.igst > 0;
  const issued = new Date(invoice.issuedAt || invoice.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div style={S.page}>
      <style>{printCss}</style>
      <div className="kj-no-print" style={S.actions}>
        <button onClick={() => navigate(-1)} style={{ ...S.btn, background: 'transparent', color: '#1B5C3A', border: '1.5px solid #1B5C3A' }}>← Back</button>
        <button onClick={() => {
          const originalTitle = document.title;
          document.title = '';
          window.print();
          document.title = originalTitle;
        }} style={S.btn}>🖨 Print / Save as PDF</button>
      </div>

      <div className="kj-sheet" style={S.sheet}>
        <div style={S.header}>
          <div>
            <div style={S.brand}>Kanjusee<span style={S.brandDot}>.</span></div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6, lineHeight: 1.6 }}>
              {invoice.billedFrom?.address || 'Surat, Gujarat'}<br />
              {invoice.billedFrom?.gstin ? `GSTIN: ${invoice.billedFrom.gstin}` : 'GSTIN: (to be configured)'} · SAC: {invoice.sacCode}
            </div>
          </div>
          <div style={S.metaRight}>
            <div style={S.tag}>Tax Invoice</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--char)', marginBottom: 4 }}>{invoice.invoiceNumber}</div>
            <div>Issued: {issued}</div>
            <div style={{ marginTop: 4 }}>Status: <strong style={{ color: '#1B5C3A' }}>{invoice.status}</strong></div>
          </div>
        </div>

        <div style={S.twoCol}>
          <div>
            <div style={S.sectionTitle}>Billed from</div>
            <div style={S.addr}>
              <div style={S.addrName}>{invoice.billedFrom?.name || 'Kanjusee'}</div>
              <div>{invoice.billedFrom?.address || 'Surat, Gujarat'}</div>
              <div>State: {invoice.billedFrom?.state || 'gujarat'}</div>
              {invoice.billedFrom?.gstin && <div>GSTIN: {invoice.billedFrom.gstin}</div>}
            </div>
          </div>
          <div>
            <div style={S.sectionTitle}>Billed to</div>
            <div style={S.addr}>
              <div style={S.addrName}>{invoice.billedTo?.firmName || invoice.billedTo?.name || '-'}</div>
              {invoice.billedTo?.firmName && invoice.billedTo?.name && <div>{invoice.billedTo.name}</div>}
              <div>{invoice.billedTo?.city ? `${invoice.billedTo.city}, ` : ''}{invoice.billedTo?.state || '-'}</div>
              <div>GSTIN: {invoice.billedTo?.gstin || 'Not provided (B2C)'}</div>
            </div>
          </div>
        </div>

        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Description</th>
              <th style={S.th}>SAC</th>
              <th style={{ ...S.th, ...S.tdRight }}>Taxable value</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lineItems || []).map((li, i) => (
              <tr key={i}>
                <td style={S.td}>{li.description}</td>
                <td style={S.td}>{li.sacCode || invoice.sacCode}</td>
                <td style={{ ...S.td, ...S.tdRight }}>{fmtAmt(li.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={S.totalsBox}>
          <div style={S.totalRow}><span style={S.totalLabel}>Taxable value</span><span style={S.totalVal}>{fmtAmt(invoice.taxableValue)}</span></div>
          {isIGST ? (
            <div style={S.totalRow}><span style={S.totalLabel}>IGST @ {invoice.totalRate || 18}%</span><span style={S.totalVal}>{fmtAmt(invoice.igst)}</span></div>
          ) : (
            <>
              <div style={S.totalRow}><span style={S.totalLabel}>CGST @ {(invoice.totalRate || 18) / 2}%</span><span style={S.totalVal}>{fmtAmt(invoice.cgst)}</span></div>
              <div style={S.totalRow}><span style={S.totalLabel}>SGST @ {(invoice.totalRate || 18) / 2}%</span><span style={S.totalVal}>{fmtAmt(invoice.sgst)}</span></div>
            </>
          )}
          <div style={{ ...S.totalRow, ...S.grandRow }}>
            <span style={S.grandLabel}>Total (GST-inclusive)</span>
            <span style={S.grandVal}>{fmtAmt(invoice.amountPaid)}</span>
          </div>
        </div>

        <div style={S.footer}>
          <p><strong>Wallet is prepaid and non-refundable.</strong> The amount paid is GST-inclusive — the full value has been credited to your Kanjusee wallet and can be used for lead purchases. The tax component shown above is remitted to the government by Kanjusee.</p>
          <p style={{ marginTop: 8 }}>Place of supply: {invoice.billedTo?.state || '-'}. Tax type: {invoice.gstType === 'intra-state' ? 'Intra-state (CGST + SGST)' : 'Inter-state (IGST)'}. {invoice.sacDescription && `SAC ${invoice.sacCode} — ${invoice.sacDescription}.`}</p>
          <p style={{ marginTop: 8 }}>This is a computer-generated invoice and does not require a signature.</p>
        </div>
      </div>
    </div>
  );
}
